import * as THREE from 'three';
import { Mesh3D } from './Mesh3D';
import { POI } from '../poi/POI';
import { Logger } from '../core/Logger';
import { Vector3, Color, Vector2 } from '../types';
import { SceneSettingsData } from '../core/SceneSettingsData';
import { AppSettingsData } from '../core/AppSettingsData';

/**
 * ConnectorShape - Handles connector line rendering between POI and its labels
 * Single responsibility: Draw lines connecting POI to labels
 */
export class ConnectorShape extends Mesh3D {
    private _connectorLine: THREE.Line | null = null;
    private _connectorGeometry: THREE.BufferGeometry | null = null;
    private _connectorMaterial: THREE.LineBasicMaterial | null = null;
    private _needsUpdate: boolean = true;
    private _targetLabel: Mesh3D | null = null;

    constructor(poi: POI, targetLabel: Mesh3D, sceneSettings?: SceneSettingsData, appSettings?: AppSettingsData) {
        super(poi, sceneSettings, appSettings);
        this._targetLabel = targetLabel;
        
        // Listen for connector property changes
        this.setupEventHandlers();
        
        // Initial setup
        this.updateConnector();
    }

    private setupEventHandlers(): void {
        // Listen for connector property changes
        this.poi.poidata.showConnector.on(() => {
            this._needsUpdate = true;
            this.updateConnector();
        });
        
        this.poi.poidata.connectorColor.on(() => {
            this._needsUpdate = true;
            this.updateConnector();
        });
        
        this.poi.poidata.connectorThickness.on(() => {
            this._needsUpdate = true;
            this.updateConnector();
        });
        
        this.poi.poidata.labelOpacity.on(() => {
            this._needsUpdate = true;
            this.updateConnector();
        });
    }

    private updateConnector(): void {
        if (!this.poi.poidata.showConnector.value) {
            this.hideConnector();
            return;
        }

        this.createConnectorGeometry();
        this.createConnectorMaterial();
        this.showConnector();
    }

    private createConnectorGeometry(): void {
        // Clean up existing geometry
        if (this._connectorGeometry) {
            this._connectorGeometry.dispose();
            this._connectorGeometry = null;
        }

        // Create new line geometry
        this._connectorGeometry = new THREE.BufferGeometry();
        
        // Initial positions (will be updated in onUpdate)
        const positions = new Float32Array([0, 0, 0, 0, 0, 0]);
        this._connectorGeometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    }

    private createConnectorMaterial(): void {
        if (!this._connectorMaterial) {
            this._connectorMaterial = new THREE.LineBasicMaterial({ 
                color: 0xffffff,
                transparent: true,
                opacity: 1.0 
            });
        }

        // Update material properties
        const connectorColor = this.poi.poidata.connectorColor.value ?? new Color('#ffffff');
        this._connectorMaterial.color.setHex(parseInt(connectorColor.toString().replace('#', '0x')));
        
        const thickness = this.poi.poidata.connectorThickness.value ?? 1.0;
        (this._connectorMaterial as any).linewidth = Math.max(thickness, 1.0);
        
        const labelOpacity = this.poi.poidata.labelOpacity.value ?? 1.0;
        this._connectorMaterial.opacity = labelOpacity;
        
        this._connectorMaterial.needsUpdate = true;
    }

    private showConnector(): void {
        if (!this._connectorLine && this._connectorGeometry && this._connectorMaterial) {
            this._connectorLine = new THREE.Line(this._connectorGeometry, this._connectorMaterial);
            this._connectorLine.userData.sceneNode = this;
            
            // Add to scene via parent
            if (this.getParent() && (this.getParent() as any)._mesh) {
                ((this.getParent() as any)._mesh as THREE.Group).add(this._connectorLine);
            }
        }
    }

    private hideConnector(): void {
        if (this._connectorLine && this._connectorLine.parent) {
            this._connectorLine.parent.remove(this._connectorLine);
            this._connectorLine = null;
        }
    }

    /**
     * Update connector line positions every frame
     */
    onUpdate(deltaTime: number): void {
        super.onUpdate(deltaTime);

        if (!this._connectorLine || !this.poi.poidata.showConnector.value || !this._targetLabel) {
            return;
        }

        // Get connector start position - should match where label is anchored
        let connectorStartPosition: Vector3;
        const meshPoint = this.poi.poidata.labelMeshPoint.value;
        
        if (meshPoint === null) {
            // DEFAULT CASE: No mesh point, connect from POI/shape center
            connectorStartPosition = this.poi.shape ? 
                this.poi.shape.transform.getWorldPosition() : 
                this.poi.transform.getWorldPosition();
        } else {
            // MESH POINT CASE: Connect from the surface position (same as label anchor)
            try {
                connectorStartPosition = this.poi.shape ?
                    this.poi.shape.getPositionFromUV(new Vector2(meshPoint.x, meshPoint.y)) :
                    this.poi.transform.getWorldPosition();
            } catch (error) {
                // FALLBACK: Use POI center if surface calculation fails
                connectorStartPosition = this.poi.shape ?
                    this.poi.shape.transform.getWorldPosition() :
                    this.poi.transform.getWorldPosition();
                //Logger.Instance.Debug(`🔗 [${this.poi.name}] Connector using fallback position - surface calculation failed: ${error instanceof Error ? error.message : String(error)}`, 'ConnectorPosition');
            }
        }
            
        const labelPosition = this._targetLabel.transform.getWorldPosition();

        // Convert world positions into local space of the THREE parent the line is attached to
        let startLocal = connectorStartPosition;
        let endLocal = labelPosition;
        try {
            const threeParent = this._connectorLine?.parent as THREE.Object3D | null;
            if (threeParent) {
                const inv = new THREE.Matrix4().copy(threeParent.matrixWorld).invert();
                const sp = new THREE.Vector3(startLocal.x, startLocal.y, startLocal.z).applyMatrix4(inv);
                const ep = new THREE.Vector3(endLocal.x, endLocal.y, endLocal.z).applyMatrix4(inv);
                startLocal = new Vector3(sp.x, sp.y, sp.z);
                endLocal = new Vector3(ep.x, ep.y, ep.z);
            }
        } catch {}

        // Update line positions (sanitize to avoid NaNs)
        const values = [
            startLocal.x, startLocal.y, startLocal.z,
            endLocal.x, endLocal.y, endLocal.z
        ];
        for (let i = 0; i < values.length; i++) {
            if (!Number.isFinite(values[i])) values[i] = 0;
        }
        const positions = new Float32Array(values);

        if (this._connectorGeometry) {
            this._connectorGeometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
            this._connectorGeometry.attributes.position.needsUpdate = true;
        }
    }

    /**
     * Update target label reference
     */
    setTargetLabel(targetLabel: Mesh3D): void {
        this._targetLabel = targetLabel;
    }

    /**
     * Clean up resources
     */
    destroy(): void {
        this.hideConnector();
        
        if (this._connectorGeometry) {
            this._connectorGeometry.dispose();
            this._connectorGeometry = null;
        }
        
        if (this._connectorMaterial) {
            this._connectorMaterial.dispose();
            this._connectorMaterial = null;
        }
        
        super.destroy();
    }

    // Required by Mesh3D
    protected createGeometry(): THREE.BufferGeometry {
        return new THREE.BufferGeometry();
    }

    protected calculateBoundingBox(): THREE.Box3 {
        return new THREE.Box3();
    }

    protected buildMesh(): void {
        // ConnectorShape doesn't need a mesh - it manages THREE.Line directly
    }
} 